import React, { useEffect, useState } from 'react';
import {
  Clock,
  Loader2,
  Tv,
  Radio,
  Globe2,
  Share2,
  AlertCircle,
  PlayCircle,
  Edit3,
} from 'lucide-react';
import {
  getCurrentUser,
  getPlanningItemsForUser,
  startWorkingOnAssignment,
} from '../services/store';
import {
  PlanningItemWithUser,
  User,
  LANGUAGE_LABELS,
} from '../types';

interface MyAssignmentsProps {
  onEditStory: (storyId: string) => void;
}

const MyAssignments: React.FC<MyAssignmentsProps> = ({ onEditStory }) => {
  const [assignments, setAssignments] = useState<PlanningItemWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    loadData();
  }, [refresh]);

  const loadData = async () => {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);

      if (user) {
        const userAssignments = await getPlanningItemsForUser(user.id);
        setAssignments(userAssignments);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartWorking = async (planningId: string, storyId: string) => {
    await startWorkingOnAssignment(planningId);
    setRefresh((prev) => prev + 1);
    onEditStory(storyId);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'tv': return <Tv className="h-5 w-5 text-blue-500" />;
      case 'radio': return <Radio className="h-5 w-5 text-orange-500" />;
      case 'web': return <Globe2 className="h-5 w-5 text-green-500" />;
      case 'social': return <Share2 className="h-5 w-5 text-purple-500" />;
      default: return <Globe2 className="h-5 w-5 text-slate-500" />;
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'tv': return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20';
      case 'radio': return 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20';
      case 'web': return 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20';
      case 'social': return 'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20';
      default: return 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto h-full overflow-y-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Assignments</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Content assigned to you for production
        </p>
      </div>

      {/* Assignments List */}
      {assignments.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <div className="bg-slate-50 dark:bg-slate-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-slate-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">No assignments</h3>
          <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            You don't have any content assigned to you yet. Editors will assign stories to you from the Planning page.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((item) => (
            <div
              key={item.id}
              className={`rounded-xl border-2 p-5 transition-all hover:shadow-md ${getPlatformColor(item.platform)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    {getPlatformIcon(item.platform)}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-lg font-semibold text-slate-900 dark:text-white">
                        {(item.platform || 'all').toUpperCase()}
                      </span>
                      <span className="px-2 py-0.5 text-xs font-medium bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full shadow-sm">
                        {LANGUAGE_LABELS[item.language || 'fa-AF']}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        item.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                          : item.status === 'assigned'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                      }`}>
                        {item.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>

                    <p className="text-sm font-mono text-slate-700 dark:text-slate-300 mb-2">
                      Story: {item.story?.slug || 'Unknown'}
                    </p>

                    {item.notes && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                        {item.notes}
                      </p>
                    )}

                    {item.due_date && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        Due: {new Date(item.due_date).toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  {!item.promoted_to_content_at ? (
                    <button
                      onClick={() => handleStartWorking(item.id, item.story_id)}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Start Working
                    </button>
                  ) : (
                    <button
                      onClick={() => onEditStory(item.story_id)}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Continue Editing
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAssignments;
